import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Chip, Input } from 'common/components';
import { Table, TableColumn, TableHeaderCell } from 'common/components/Table';
import { useAuth } from 'contexts/AuthContext';
import { useGetPeople } from 'services/hooks/queries';
import type { Person } from 'types/accessManager.type';
import {
    companyLine,
    filterPeople,
    isDisabled,
    paginate,
    ROWS_PER_PAGE_OPTIONS,
    sortPeople,
    ttsLabel,
    type SortKey,
    type TTSFilter,
} from './filters';
import styles from './People.module.scss';

const TTS_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'with', label: 'With TTS' },
    { value: 'without', label: 'Without TTS' },
];

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

// Everybody the Platform knows (SLAI staff only), read-only: who they are,
// which companies they are in, and whether TTS lets them in — people who
// signed up on the TTS landing belong to no company and show up only here.
const People: React.FC = () => {
    const { userRoles } = useAuth();
    // Same check as CompanyProducts, so a role in another case still counts.
    const isSuperAdmin = (Array.isArray(userRoles) ? userRoles : [])
        .map((r) => (typeof r === 'string' ? r.toUpperCase() : String(r)))
        .includes('SUPER_ADMIN');
    const { peopleData, isPeopleLoading, isError } = useGetPeople(isSuperAdmin);
    const [query, setQuery] = useState('');
    const [tts, setTTS] = useState<TTSFilter>('all');
    const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
        key: 'created',
        direction: 'desc',
    });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const all = useMemo(() => peopleData?.people ?? [], [peopleData]);
    const people = useMemo(
        () => sortPeople(filterPeople(all, query, tts), sort.key, sort.direction),
        [all, query, tts, sort]
    );
    const withTTS = useMemo(() => all.filter((p) => p.tts).length, [all]);
    const { rows, currentPage, totalPages } = paginate(people, page, rowsPerPage);

    if (!isSuperAdmin) {
        return <Navigate to='/dashboard' replace />;
    }

    // A new filter or order starts from the first page.
    const changeQuery = (value: string) => {
        setQuery(value);
        setPage(1);
    };
    const changeTTS = (value: TTSFilter) => {
        setTTS(value);
        setPage(1);
    };

    // A second click on the sorted column turns it round.
    const sortBy = (key: SortKey) => ({
        isSorted: sort.key === key,
        direction: sort.key === key ? sort.direction : ('asc' as const),
        onSort: () => {
            setSort((current) =>
                current.key === key
                    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                    : { key, direction: key === 'created' ? 'desc' : 'asc' }
            );
            setPage(1);
        },
    });

    const columns: TableColumn<Person>[] = [
        { key: 'email', title: <TableHeaderCell label='Email' sort={sortBy('email')} />, dataIndex: 'email' },
        {
            key: 'name',
            title: 'Name',
            render: (_, p) => (
                <span>
                    {p.name || <span className={styles.muted}>—</span>}
                    {p.staff ? <span className={styles.muted}> · staff</span> : null}
                    {isDisabled(p) ? <span className={styles.muted}> · disabled</span> : null}
                </span>
            ),
        },
        {
            key: 'tts',
            title: (
                <TableHeaderCell
                    label='TTS'
                    sort={sortBy('tts')}
                    filterOptions={{
                        options: TTS_FILTERS,
                        selectedValue: tts,
                        onChange: (value: string) => changeTTS(value as TTSFilter),
                    }}
                    filterActive={tts !== 'all'}
                />
            ),
            render: (_, p) => (
                <Chip variant='filled' size='sm' color={isDisabled(p) ? 'warning' : p.tts ? 'success' : 'secondary'}>
                    {ttsLabel(p)}
                </Chip>
            ),
        },
        {
            key: 'companies',
            title: 'Companies',
            render: (_, p) =>
                p.companies.length ? (
                    <ul className={styles.companies}>
                        {p.companies.map((c) => (
                            <li key={c.id}>{companyLine(c)}</li>
                        ))}
                    </ul>
                ) : (
                    <span className={styles.muted}>None</span>
                ),
        },
        {
            key: 'created',
            title: <TableHeaderCell label='Signed up' sort={sortBy('created')} />,
            render: (_, p) => formatDate(p.created_at),
        },
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>People</h1>
            </div>
            <p className={styles.note}>
                Everybody with a login. People who signed up for TTS on their own belong to no company: TTS is a product
                of their personal account.
                {/* Counted only once there is a list: a failed or pending load is not an empty directory. */}
                {peopleData && !isError ? ` ${all.length} people, ${withTTS} with TTS.` : null}
            </p>
            <div className={styles.filter}>
                <Input
                    type='text'
                    placeholder='Filter by email or name'
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeQuery(e.target.value)}
                />
            </div>
            {isError && (
                <p className={styles.error} role='alert'>
                    The people could not be loaded. Reload the page to try again.
                </p>
            )}
            <Table
                data={rows}
                columns={columns}
                isLoading={isPeopleLoading}
                emptyText={isError ? 'Nothing to show' : 'No people found'}
                rowKey='user_id'
                hoverable
                variant='secondary'
                pagination={{
                    enabled: true,
                    currentPage,
                    totalPages,
                    totalRecords: people.length,
                    rowsPerPage,
                    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
                    onPageChange: setPage,
                    onRowsPerPageChange: (value: number) => {
                        setRowsPerPage(value);
                        setPage(1);
                    },
                }}
            />
        </div>
    );
};

export default People;
